import { store } from '../core/store.js';
import { bus } from '../core/event-bus.js';
import { UI_DEBUG } from '../core/debug-flags.js';
import { initAudio, resumeIfSuspended } from '../audio/engine.js';
import { doPing } from '../scan/ping-cycle.js';
import { doScan, stopScan } from '../scan/scan-engine.js';
import { calibrateRefinedWithSanity } from '../calibration/engine.js';
import { refreshDeviceInfo } from './device-info.js';
import { readConfigFromDOM, syncDOMFromConfig, syncModeUI, setButtonStates } from './controls.js';
import { setStatus, log, updateDirectionReadout, updateBestReadout, renderCalibInfo } from './readouts.js';
import { detectDevice, applyDevicePreset } from './device-presets.js';
import { setupGeometryPointerHandlers, ensureGeometryWizardHandlesInitialized, resetGeometryWizardHandles, applyGeometryWizard, setGeomWizardStatus, syncGeometryWizardControls } from './geometry-wizard.js';
import { drawProfile, drawProfilePlaceholder, setProfileMouse, setProfileEnvelope } from '../viz/profile-plot.js';
import { drawHeatmap, setHeatmapMouse, redrawHeatmapCrosshair, setHeatmapObstructions } from '../viz/heatmap-plot.js';
import { drawSpectralPanel, clearSpectralPanel } from '../viz/spectral-panel.js';
import { drawForwardScatterPanel, clearForwardScatterPanel } from '../viz/forward-scatter-panel.js';
import { drawGeometry } from '../viz/geometry-plot.js';
import { drawCalibSanityPlot, drawSanityPlaceholder } from '../viz/sanity-plot.js';
import { resizeCanvasForDPR } from '../viz/renderer.js';
import { createHeatmap, updateHeatmapRow } from '../scan/heatmap-data.js';
import { initLevelMeter } from '../viz/level-meter.js';
import { initMicSpectrogram } from '../viz/mic-spectrogram.js';
import { drawSignalPreview, scheduleSignalPreview } from '../viz/signal-preview.js';
import { DEFAULT_HEAT_BINS, DEVICE_PRESETS } from '../constants.js';
import { setupPeerUI, syncPeerButtons, handleUrlOffer, handleUrlAnswer } from './peer-ui.js';
import { getOfferFromUrl, getAnswerFromUrl, clearSignalFromUrl } from './url-params.js';
import { initOnboarding } from './onboarding.js';
import { initPwaUpdateNotifier } from './pwa-update.js';

function el(id: string): HTMLElement | null {
  return document.getElementById(id);
}

/* ---- Plain-language state line (elaborates the #status element) ---- */
type StatusTone = 'info' | 'warn' | 'ok';
function setStatusHint(html: string, tone: StatusTone = 'info'): void {
  const e = el('statusHint');
  if (!e) return;
  e.innerHTML = html;
  e.classList.toggle('is-warn', tone === 'warn');
  e.classList.toggle('is-ok', tone === 'ok');
}

/* ---- Waveform segmented control ↔ hidden #mode <select> (source of truth) ---- */
function syncModeSegmented(): void {
  const seg = el('modeSegmented');
  const select = el('mode') as HTMLSelectElement | null;
  if (!seg || !select) return;
  const value = select.value;
  seg.querySelectorAll<HTMLButtonElement>('.seg-btn').forEach((b) => {
    const active = b.dataset.mode === value;
    b.classList.toggle('is-active', active);
    b.setAttribute('aria-checked', active ? 'true' : 'false');
    b.tabIndex = active ? 0 : -1;
  });
}

function initModeSegmented(): void {
  const seg = el('modeSegmented');
  const select = el('mode') as HTMLSelectElement | null;
  if (!seg || !select) return;
  const buttons = Array.from(seg.querySelectorAll<HTMLButtonElement>('.seg-btn'));
  for (const b of buttons) {
    b.addEventListener('click', () => {
      const next = b.dataset.mode;
      if (!next || select.value === next) return;
      select.value = next;
      // Drive the existing #mode change pipeline (clear panels, syncModeUI, readConfig, preview).
      select.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
  // Arrow-key navigation for the radiogroup.
  seg.addEventListener('keydown', (ev) => {
    if (ev.key !== 'ArrowRight' && ev.key !== 'ArrowLeft' && ev.key !== 'ArrowUp' && ev.key !== 'ArrowDown') return;
    ev.preventDefault();
    const idx = buttons.findIndex((b) => b.classList.contains('is-active'));
    if (idx < 0) return;
    const fwd = ev.key === 'ArrowRight' || ev.key === 'ArrowDown';
    const target = buttons[(idx + (fwd ? 1 : -1) + buttons.length) % buttons.length];
    target.focus();
    target.click();
  });
  syncModeSegmented();
}

function applyRetinaCanvases(): boolean {
  const ids = ['profile', 'heatmap', 'calibPlot', 'geometry', 'micSpectrogram', 'previewChirp', 'previewMls', 'previewGolay', 'previewMultiplex'];
  let changed = false;
  for (const id of ids) {
    const canvas = el(id) as HTMLCanvasElement | null;
    if (canvas && resizeCanvasForDPR(canvas)) changed = true;
  }
  return changed;
}

function redrawAllCanvases(): void {
  const state = store.get();
  const lp = state.lastProfile;

  if (lp.corr && lp.corr.length) {
    drawProfile(lp.corr, lp.tau0, lp.c, lp.minR, lp.maxR);
  } else {
    drawProfilePlaceholder();
  }

  drawHeatmap(state.config.minRange, state.config.maxRange);
  drawGeometry(state.config.minRange, state.config.maxRange);

  const calib = state.calibration;
  if (calib?.sanity.have && calib.sanity.curveL && calib.sanity.curveR) {
    drawCalibSanityPlot(calib.sanity.curveL, calib.sanity.peakIndexL, calib.sanity.curveR, calib.sanity.peakIndexR, calib.sanity.earlyMs);
  } else {
    drawSanityPlaceholder();
  }

  drawSignalPreview();
}

/* ---- canvas pointer → pixel coords ---- */
function canvasPointerPos(canvas: HTMLCanvasElement, ev: PointerEvent | MouseEvent): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const sx = canvas.width / Math.max(1, rect.width);
  const sy = canvas.height / Math.max(1, rect.height);
  return { x: (ev.clientX - rect.left) * sx, y: (ev.clientY - rect.top) * sy };
}

/* ---- rAF-based config debounce ---- */
let configRafId = 0;
function scheduleReadConfig(): void {
  if (configRafId) return;
  configRafId = requestAnimationFrame(() => {
    configRafId = 0;
    readConfigFromDOM();
  });
}

/* ---- ping stats ---- */
let pingStartTime = 0;

/* ---- per-ping accessibility transition tracking ---- */
// We avoid announcing on every ping (would be deafening with screen readers).
// Only announce on state transitions of the detection gate (pass<->fail), and
// on phase-coherence changes that exceed PHASE_COH_ANNOUNCE_DELTA in absolute
// magnitude. lastAnnouncedX values track what the announcer last emitted.
let lastAnnouncedDetectionState: 'pass' | 'fail' | 'unknown' = 'unknown';
let lastAnnouncedPhaseCoherence: number | null = null;
const PHASE_COH_ANNOUNCE_DELTA = 0.2;

function announceDetectionStatus(message: string): void {
  const announcer = el('detectionStatusAnnouncer');
  if (announcer) announcer.textContent = message;
}

/* ---- per-ping panel cleanup ---- */
/**
 * Clear all per-ping visualizations and readouts. Used when:
 *  - a scan stops (the last ping's panels would otherwise linger forever)
 *  - the user switches probe mode (chirp/MLS/Golay/multiplex) and stale
 *    data from the previous mode no longer reflects the current setup.
 */
function clearPerPingPanels(): void {
  clearSpectralPanel();
  clearForwardScatterPanel();
  setHeatmapObstructions(null);
  setProfileEnvelope(null);

  const resetBadge = (id: string) => {
    const badge = el(id);
    if (badge) {
      badge.textContent = '—';
      badge.classList.remove('is-pass', 'is-fail');
    }
  };
  resetBadge('detectionGateBadge');
  resetBadge('detectionGateBadgeTop');

  const readoutText = 'phase coh: —';
  const readout = el('phaseCoherenceReadout');
  if (readout) readout.textContent = readoutText;
  const readoutTop = el('phaseCoherenceReadoutTop');
  if (readoutTop) readoutTop.textContent = readoutText;

  // Reset transition trackers so the next real reading announces fresh.
  lastAnnouncedDetectionState = 'unknown';
  lastAnnouncedPhaseCoherence = null;
}

export function initApp(): void {
  store.subscribe('status', (value) => {
    if (typeof value === 'string') setStatus(value);
  });

  // One-way startup init: config -> DOM
  syncDOMFromConfig();

  // Mode UI — also clear per-ping panels so stale data from previous mode
  // doesn't bleed into the new mode's first ping.
  el('mode')?.addEventListener('change', () => {
    clearPerPingPanels();
    syncModeUI();
    syncModeSegmented();
    readConfigFromDOM();
    scheduleSignalPreview();
  });
  syncModeUI();
  initModeSegmented();

  // PWA update notification
  initPwaUpdateNotifier();

  // Angle slider
  el('angle')?.addEventListener('input', () => {
    const angleEl = el('angle') as HTMLInputElement;
    const angleValEl = el('angleVal');
    if (angleValEl) angleValEl.textContent = angleEl.value;
    scheduleReadConfig();
    drawGeometry(store.get().config.minRange, store.get().config.maxRange);
  });

  // Geometry canvas
  setupGeometryPointerHandlers();

  // Button wiring
  el('btnInit')?.addEventListener('click', async () => {
    const btn = el('btnInit') as HTMLButtonElement | null;
    try {
      if (btn) { btn.disabled = true; btn.innerHTML = 'Initializing\u2026'; }
      setStatus('initializing');
      readConfigFromDOM();
      await initAudio();
      if (btn) { btn.innerHTML = 'Init Audio <kbd>I</kbd>'; }
      setButtonStates(true, false);
      renderCalibInfo();
      await refreshDeviceInfo();
      const audioState = store.get().audio;
      log(`[ok] audio initialized: sr=${audioState.actualSampleRate} Hz, capture=${audioState.captureMethod}, channels=${audioState.channelCount}`);
      // Hide the init hint and show next-step guidance
      const initHintEl = el('initHint');
      if (initHintEl) {
        initHintEl.textContent = '\u2192 Audio ready. Next: click Calibrate for best accuracy, or Scan to start immediately.';
      }
      setStatusHint('Audio ready. Run <b>Calibrate</b> (recommended) for accurate range, then <b>Scan</b>.', 'info');
      const stereoEl = el('stereoIndicator');
      if (stereoEl) {
        if (audioState.channelCount >= 2) {
          stereoEl.style.display = 'inline';
          stereoEl.textContent = `${audioState.channelCount}ch`;
        } else {
          stereoEl.style.display = 'none';
        }
      }
      setStatus('ready');
      syncPeerButtons();
      initLevelMeter();
      initMicSpectrogram();
      drawSignalPreview();
    } catch (e: any) {
      if (btn) { btn.disabled = false; btn.innerHTML = 'Init Audio <kbd>I</kbd>'; }
      setStatus('error');
      const msg = e?.message || String(e);
      if (/permission|denied|not allowed/i.test(msg)) {
        log('[err] Microphone permission denied. Please allow mic access in your browser settings and try again.');
      } else if (/not found|no device/i.test(msg)) {
        log('[err] No audio input device found. Connect a microphone and try again.');
      } else {
        log('[err] init failed: ' + msg);
      }
      const initHintEl = el('initHint');
      if (initHintEl) {
        initHintEl.style.color = '#ffbf80';
        initHintEl.textContent = '\u2192 Init failed. Check the Diagnostics log below for details, then retry Init Audio.';
      }
      setStatusHint('Init failed \u2014 check the Diagnostics log under Advanced, then retry <b>Init Audio</b>.', 'warn');
    }
  });

  el('btnPing')?.addEventListener('click', async () => {
    try {
      readConfigFromDOM();
      setStatus('pinging');
      setStatusHint('Pinging — emitting one probe and listening for an echo…');
      await doPing(store.get().config.steeringAngleDeg, null);
      updateBestReadout();
      updateDirectionReadout();
      setStatus('ready');
    } catch (e: any) {
      setStatus('error');
      log('[err] ping failed: ' + (e?.message || e));
    }
  });

  el('btnScan')?.addEventListener('click', async () => {
    if (store.get().status === 'calibrating') return;
    try {
      readConfigFromDOM();
      setButtonStates(true, true);
      setStatusHint('Scanning — sweeping across angles and listening for echoes…');
      await doScan();
      drawHeatmap(store.get().config.minRange, store.get().config.maxRange);
      drawGeometry(store.get().config.minRange, store.get().config.maxRange);
      updateBestReadout();
      updateDirectionReadout();
      setButtonStates(true, false);
    } catch (e: any) {
      setStatus('error');
      log('[err] scan failed: ' + (e?.message || e));
      stopScan();
      setButtonStates(true, false);
    }
  });

  el('btnStop')?.addEventListener('click', () => {
    stopScan();
    setButtonStates(true, false);
  });

  el('btnCalibrate')?.addEventListener('click', async () => {
    if (store.get().scanning) return;
    try {
      readConfigFromDOM();
      setStatusHint('Calibrating — stay still and keep the room quiet…');
      await calibrateRefinedWithSanity();
      syncDOMFromConfig();
      renderCalibInfo();
      const calib = store.get().calibration;
      if (calib?.sanity.have && calib.sanity.curveL && calib.sanity.curveR) {
        const sanityDetails = el('sanityDetails') as HTMLDetailsElement | null;
        if (sanityDetails) sanityDetails.open = true;
        // Draw AFTER opening details so canvas has correct dimensions for DPR scaling
        drawCalibSanityPlot(calib.sanity.curveL, calib.sanity.peakIndexL, calib.sanity.curveR, calib.sanity.peakIndexR, calib.sanity.earlyMs);
      }
      drawGeometry(store.get().config.minRange, store.get().config.maxRange);
      // Calibration validity changed → re-evaluate the primary-action highlight
      // (Calibrate → Scan now that we have a lock).
      setButtonStates(true, store.get().scanning);
      // Update hint after successful calibration
      const calibHintEl = el('initHint');
      const q = store.get().calibration?.quality ?? 0;
      if (calibHintEl) {
        calibHintEl.style.color = q > 0.5 ? '#4caf50' : '#ffbf80';
        calibHintEl.textContent = `\u2713 Calibrated (quality ${q.toFixed(2)}). Click Scan to start echolocation.`;
      }
      if (q > 0.5) {
        setStatusHint(`Calibrated (quality ${q.toFixed(2)}). Click <b>Scan</b> to map your surroundings.`, 'ok');
      } else {
        setStatusHint(`Calibration is weak (quality ${q.toFixed(2)}) \u2014 try a quieter room or recalibrate. You can still <b>Scan</b>.`, 'warn');
      }
    } catch (e: any) {
      setStatus('error');
      log('[err] calibrate failed: ' + (e?.message || e));
      renderCalibInfo();
      setStatusHint('Calibration failed \u2014 see the Diagnostics log under Advanced, then try again.', 'warn');
    }
  });

  el('btnRefreshDevices')?.addEventListener('click', async () => {
    try { await refreshDeviceInfo(); }
    catch (e: any) { log('[err] refresh devices failed: ' + (e?.message || e)); }
  });

  // Device preset
  el('devicePreset')?.addEventListener('change', () => {
    const key = (el('devicePreset') as HTMLSelectElement).value;
    applyDevicePreset(key);
    // A laptop scan preset may set #mode (and probe params) directly; reflect that in
    // the segmented control and probe-box visibility, then recompute derived values.
    syncModeUI();
    syncModeSegmented();
    readConfigFromDOM(); // recompute derived values (minRange, scanDwell) after spacing change
    ensureGeometryWizardHandlesInitialized(true);
    drawGeometry(store.get().config.minRange, store.get().config.maxRange);
  });

  // Geometry wizard
  el('geomWizardOn')?.addEventListener('change', () => {
    const checked = (el('geomWizardOn') as HTMLInputElement).checked;
    store.update(s => {
      s.geomWizard.active = checked;
      s.geomWizard.dragging = null;
    });
    if (checked) {
      ensureGeometryWizardHandlesInitialized(true);
      setGeomWizardStatus('Drag handles on geometry view, then Apply geometry.');
    } else {
      setGeomWizardStatus('Enable wizard to drag speakers/mic on geometry view.');
    }
    syncGeometryWizardControls();
    drawGeometry(store.get().config.minRange, store.get().config.maxRange);
  });

  el('btnGeomReset')?.addEventListener('click', () => {
    if (!store.get().geomWizard.active) return;
    resetGeometryWizardHandles();
  });

  el('btnGeomApply')?.addEventListener('click', () => {
    if (!store.get().geomWizard.active) return;
    applyGeometryWizard();
  });

  // Trace & direction
  el('showTrace')?.addEventListener('change', () => {
    readConfigFromDOM();
    drawHeatmap(store.get().config.minRange, store.get().config.maxRange);
  });

  el('presetApplyScan')?.addEventListener('change', () => {
    readConfigFromDOM();
  });

  // Heatmap display settings — redraw immediately on change
  el('colormapSelect')?.addEventListener('change', () => {
    readConfigFromDOM();
    drawHeatmap(store.get().config.minRange, store.get().config.maxRange);
  });
  el('heatmapDbScaleOn')?.addEventListener('change', () => {
    readConfigFromDOM();
    drawHeatmap(store.get().config.minRange, store.get().config.maxRange);
  });
  el('heatmapDynamicRangeDb')?.addEventListener('input', () => {
    readConfigFromDOM();
    drawHeatmap(store.get().config.minRange, store.get().config.maxRange);
  });

  el('dirAxis')?.addEventListener('change', () => {
    readConfigFromDOM();
    updateDirectionReadout();
    drawGeometry(store.get().config.minRange, store.get().config.maxRange);
  });

  el('useCalib')?.addEventListener('change', () => {
    readConfigFromDOM();
    renderCalibInfo();
  });

  // Signal preview on param changes
  const paramInputIds = [
    'f1', 'f2', 'T',
    'mlsOrder', 'chipRate',
    'golayOrder', 'golayChipRate', 'golayGapMs',
    'multiplexCarrierCount', 'multiplexFStart', 'multiplexFEnd',
    'multiplexSymbolMs', 'multiplexGuardHz', 'multiplexMinSpacingHz',
    'multiplexCalibrationCandidates',
  ];
  for (const id of paramInputIds) {
    el(id)?.addEventListener('input', () => { scheduleReadConfig(); scheduleSignalPreview(); });
  }
  const paramSelectIds = ['multiplexFusion', 'multiplexUseCalibrated', 'multiplexFallbackToChirp'];
  for (const id of paramSelectIds) {
    el(id)?.addEventListener('change', () => { scheduleReadConfig(); scheduleSignalPreview(); });
  }

  // Computed config inputs — update derived labels live
  const configInputIds = ['temperature', 'maxR', 'spacing'];
  for (const id of configInputIds) {
    el(id)?.addEventListener('input', () => { scheduleReadConfig(); });
  }

  const liveConfigControlIds = [
    'spectrogramEnabled', 'spectrogramFftSize', 'spectrogramHopSize', 'spectrogramMinDb', 'spectrogramMaxDb', 'spectrogramFps',
    'micArraySpacing', 'gain',
    'scanStep', 'scanPasses',
    'distributedEnabled', 'distributedCaptureTimeoutMs',
    'strengthGate', 'confidenceGate', 'cfarPfa',
    'scanClutterOn', 'scanClutterStrength',
    'qualityAlgo', 'scanAggregateMode', 'scanTrimFraction',
    'temporalIirAlpha', 'outlierHistoryN', 'continuityBins',
    'adaptiveQualityOn', 'adaptiveQualityHysteresisMs',
    'noiseKalmanEnabled', 'noiseKalmanQ', 'noiseKalmanR', 'noiseKalmanStrength',
    'noiseKalmanFreezeHighConf', 'noiseKalmanHighConfGate', 'noiseKalmanMinFloor', 'noiseKalmanMaxFloor', 'noiseKalmanUseInCalibration',
    'subtractionBackoffOn', 'subtractionCollapseThreshold', 'subtractionPeakDropThreshold',
    'calRepeats', 'calRepeatGapMs', 'extraCalPings', 'envBaselineStrength', 'useEnvBaseline', 'useMultiband',
    'vaEnabled', 'vaHalfWindow', 'vaWindow', 'vaCoherenceFloor',
  ];
  for (const id of liveConfigControlIds) {
    const control = el(id) as HTMLInputElement | HTMLSelectElement | null;
    if (!control) continue;
    const eventName = control instanceof HTMLSelectElement || control.type === 'checkbox' || control.type === 'range'
      ? 'change'
      : 'input';
    control.addEventListener(eventName, () => {
      scheduleReadConfig();
    });
  }

  const displayBlankingInputIds = [
    'displayBlankingStartRange',
    'displayBlankingEndRange',
    'displayBlankingAttenuation',
    'displayBlankingEdgeSoftness',
  ];
  for (const id of displayBlankingInputIds) {
    el(id)?.addEventListener('input', () => { scheduleReadConfig(); });
  }
  el('displayBlankingOn')?.addEventListener('change', () => {
    scheduleReadConfig();
  });

  const trackVizInputIds = ['trackTrailMaxPoints', 'trackFadeMissCount', 'trackTrailMinAlpha', 'trackTrailMaxAlpha', 'trackMinConfidenceFloor'];
  for (const id of trackVizInputIds) {
    el(id)?.addEventListener('input', () => {
      scheduleReadConfig();
      drawGeometry(store.get().config.minRange, store.get().config.maxRange);
    });
  }

  const trackVizDefaults = {
    trailMaxPoints: 22,
    fadeMissCount: 8,
    trailMinAlpha: 0.08,
    trailMaxAlpha: 0.55,
    minConfidenceFloor: 0.0001,
  };
  const trackVizRecommended = {
    trailMaxPoints: 28,
    fadeMissCount: 10,
    trailMinAlpha: 0.05,
    trailMaxAlpha: 0.45,
    minConfidenceFloor: 0.001,
  };

  function applyTrackVizPreset(preset: {
    trailMaxPoints: number;
    fadeMissCount: number;
    trailMinAlpha: number;
    trailMaxAlpha: number;
    minConfidenceFloor: number;
  }): void {
    const setNum = (id: string, value: number) => {
      const input = el(id) as HTMLInputElement | null;
      if (input) input.value = `${value}`;
    };

    setNum('trackTrailMaxPoints', preset.trailMaxPoints);
    setNum('trackFadeMissCount', preset.fadeMissCount);
    setNum('trackTrailMinAlpha', preset.trailMinAlpha);
    setNum('trackTrailMaxAlpha', preset.trailMaxAlpha);
    setNum('trackMinConfidenceFloor', preset.minConfidenceFloor);

    readConfigFromDOM();
    drawGeometry(store.get().config.minRange, store.get().config.maxRange);
  }

  el('btnTrackVizReset')?.addEventListener('click', () => {
    applyTrackVizPreset(trackVizDefaults);
  });

  el('btnTrackVizRecommended')?.addEventListener('click', () => {
    applyTrackVizPreset(trackVizRecommended);
  });

  // ---- Pointer crosshair wiring (supports touch) ----
  const profileCanvas = el('profile') as HTMLCanvasElement | null;
  if (profileCanvas) {
    profileCanvas.style.touchAction = 'pan-y';
    profileCanvas.addEventListener('pointermove', (ev) => {
      setProfileMouse(canvasPointerPos(profileCanvas, ev));
      const state = store.get();
      const lp = state.lastProfile;
      if (lp.corr && lp.corr.length) drawProfile(lp.corr, lp.tau0, lp.c, lp.minR, lp.maxR);
    });
    profileCanvas.addEventListener('pointerleave', () => {
      setProfileMouse(null);
      const state = store.get();
      const lp = state.lastProfile;
      if (lp.corr && lp.corr.length) drawProfile(lp.corr, lp.tau0, lp.c, lp.minR, lp.maxR);
    });
  }

  const heatmapCanvas = el('heatmap') as HTMLCanvasElement | null;
  if (heatmapCanvas) {
    heatmapCanvas.style.touchAction = 'pan-y';
    heatmapCanvas.addEventListener('pointermove', (ev) => {
      setHeatmapMouse(canvasPointerPos(heatmapCanvas, ev));
      redrawHeatmapCrosshair();
    });
    heatmapCanvas.addEventListener('pointerleave', () => {
      setHeatmapMouse(null);
      redrawHeatmapCrosshair();
    });
  }

  const vizDetailIds = ['profileDetails', 'heatmapDetails', 'geometryDetails', 'micSpectrogramDetails'];
  for (const detailId of vizDetailIds) {
    const detailsEl = el(detailId) as HTMLDetailsElement | null;
    if (!detailsEl) continue;
    detailsEl.addEventListener('toggle', () => {
      if (!detailsEl.open) return;
      requestAnimationFrame(() => {
        applyRetinaCanvases();
        redrawAllCanvases();
      });
    });
  }

  // ---- Keyboard shortcuts ----
  function clickIfEnabled(id: string): void {
    const btn = el(id) as HTMLButtonElement | null;
    if (btn && !btn.disabled) btn.click();
  }

  document.addEventListener('keydown', (ev) => {
    const tag = (ev.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
    // Space on a focused button should activate that button, not trigger Ping
    if (tag === 'BUTTON' && ev.key === ' ') return;
    if (ev.ctrlKey || ev.metaKey || ev.altKey || ev.shiftKey) return;

    switch (ev.key) {
      case 'i':
      case 'I':
        clickIfEnabled('btnInit');
        break;
      case 'p':
      case 'P':
      case ' ':
        ev.preventDefault();
        clickIfEnabled('btnPing');
        break;
      case 's':
      case 'S':
        clickIfEnabled('btnScan');
        break;
      case 'Escape':
        clickIfEnabled('btnStop');
        break;
      case 'c':
      case 'C':
        clickIfEnabled('btnCalibrate');
        break;
    }
  });

  // ---- Scan progress bar ----
  bus.on('scan:step', ({ angleDeg, index, total, pass, totalPasses }) => {
    const progressEl = el('scanProgress');
    const textEl = el('scanProgressText');
    const fillEl = el('scanProgressFill');
    progressEl?.classList.remove('is-hidden');
    const passLabel = totalPasses > 1 ? ` pass ${pass + 1}/${totalPasses}` : '';
    if (textEl) textEl.textContent = `Scanning ${index + 1}/${total} (${angleDeg}\u00b0)${passLabel}`;
    const progress = totalPasses > 1
      ? (index * totalPasses + pass + 1) / (total * totalPasses)
      : (index + 1) / total;
    if (fillEl) {
      fillEl.style.width = `${progress * 100}%`;
      fillEl.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
    }
  });

  bus.on('scan:complete', () => {
    const progressEl = el('scanProgress');
    const textEl = el('scanProgressText');
    const fillEl = el('scanProgressFill');
    progressEl?.classList.add('is-hidden');
    if (textEl) textEl.textContent = 'Scanning...';
    if (fillEl) {
      fillEl.style.width = '0%';
      fillEl.setAttribute('aria-valuenow', '0');
    }

    // Clear per-ping panels — the last ping captured during scan was a noisy
    // intermediate angle, not a meaningful single-ping snapshot. The
    // consensus-based heatmap/profile/readouts get re-rendered below.
    clearPerPingPanels();

    // Final update of profile/geometry/readouts after consensus is computed.
    // During scanning these were suppressed per-ping to avoid target jumping.
    const state = store.get();
    const lp = state.lastProfile;
    if (lp.corr && lp.corr.length > 0) {
      let corrHasSignal = false;
      for (let i = 0; i < lp.corr.length; i++) {
        if (Math.abs(lp.corr[i]) > 1e-12) { corrHasSignal = true; break; }
      }
      if (corrHasSignal) {
        drawProfile(lp.corr, lp.tau0, lp.c, lp.minR, lp.maxR);
      } else {
        drawProfilePlaceholder();
      }
    }
    drawHeatmap(state.config.minRange, state.config.maxRange);
    drawGeometry(state.config.minRange, state.config.maxRange);
    updateBestReadout();
    updateDirectionReadout();

    // Plain-language outcome — surface the silent-empty-result case explicitly.
    const target = state.lastTarget;
    const gate = state.config.strengthGate;
    if (Number.isFinite(target.range) && target.strength > gate) {
      setStatusHint(`Scan complete — strongest echo at <b>${target.range.toFixed(2)} m</b>. Brighter spots on the heatmap are stronger reflections.`, 'ok');
    } else {
      setStatusHint('Scan found no clear echoes — raise <b>Output gain</b>, reduce background noise, or check your input device (Advanced ▸ Acoustic &amp; device / Diagnostics).', 'warn');
    }
  });

  // ---- Ping statistics ----
  bus.on('ping:start', () => {
    pingStartTime = performance.now();
  });

  bus.on('ping:complete', ({ angleDeg, profile, spectralSignature, envelopeAnalysis, forwardScatter, perBandEnvelopes, envelopePeak, detectionQuality }) => {
    if (UI_DEBUG) console.debug(`[ping:complete] angleDeg=${angleDeg} bestBin=${profile.bestBin} bestStrength=${profile.bestStrength.toExponential(3)} binsLen=${profile.bins.length}`);

    // Update spectral signature panel — pass perBandEnvelopes so each band row
    // can show its phase-coherence number alongside amplitude/SNR.
    if (spectralSignature) {
      drawSpectralPanel(spectralSignature, perBandEnvelopes ?? null);
    } else {
      clearSpectralPanel();
    }

    // Update forward-scatter panel and heatmap obstruction overlay
    if (forwardScatter) {
      drawForwardScatterPanel(forwardScatter);
      setHeatmapObstructions(forwardScatter.obstructions);
    } else {
      clearForwardScatterPanel();
      setHeatmapObstructions(null);
    }

    // Stash envelope for the next profile draw, and update the phase-coherence readout
    {
      let phaseCoh: number | null = null;
      if (envelopeAnalysis) {
        setProfileEnvelope(envelopeAnalysis.envelope);
        const c = envelopeAnalysis.phaseCoherence;
        phaseCoh = Number.isFinite(c) ? c : null;
      } else {
        setProfileEnvelope(null);
      }
      const text = phaseCoh !== null ? `phase coh: ${phaseCoh.toFixed(2)}` : 'phase coh: —';
      const readout = el('phaseCoherenceReadout');
      if (readout) readout.textContent = text;
      const readoutTop = el('phaseCoherenceReadoutTop');
      if (readoutTop) readoutTop.textContent = text;

      // Announce phase-coherence changes only when delta is significant; this
      // keeps assistive-tech output sparse and meaningful.
      if (phaseCoh !== null) {
        if (lastAnnouncedPhaseCoherence === null
            || Math.abs(phaseCoh - lastAnnouncedPhaseCoherence) >= PHASE_COH_ANNOUNCE_DELTA) {
          lastAnnouncedPhaseCoherence = phaseCoh;
          announceDetectionStatus(`Phase coherence ${phaseCoh.toFixed(2)}`);
        }
      }
    }

    // Detection-quality badge — small visual gate result next to phase coh.
    // We mirror the same badge state into both the always-visible top badge
    // (#detectionGateBadgeTop) and the detailed in-details badge.
    {
      const fullBadge = el('detectionGateBadge');
      const topBadge = el('detectionGateBadgeTop');
      const setBadge = (badge: HTMLElement | null, text: string, cls: 'is-pass' | 'is-fail' | null) => {
        if (!badge) return;
        badge.classList.remove('is-pass', 'is-fail');
        if (cls) badge.classList.add(cls);
        badge.textContent = text;
      };

      let nextState: 'pass' | 'fail' | 'unknown';
      let fullText: string;
      let topText: string;
      let cls: 'is-pass' | 'is-fail' | null;

      if (!detectionQuality) {
        nextState = 'unknown';
        fullText = '—';
        topText = '—';
        cls = null;
      } else if (detectionQuality.pass) {
        nextState = 'pass';
        fullText = 'ok';
        topText = 'ok';
        cls = 'is-pass';
      } else {
        nextState = 'fail';
        const reason = detectionQuality.reasons[0];
        let label = 'weak';
        if (reason === 'snr_low') label = 'weak: low SNR';
        else if (reason === 'phase_coherence_low') label = 'weak: low coherence';
        fullText = label;
        topText = 'weak';
        cls = 'is-fail';
      }
      setBadge(fullBadge, fullText, cls);
      setBadge(topBadge, topText, cls);

      // Announce ONLY transitions (pass<->fail). Same-state pings stay silent.
      if (nextState !== 'unknown' && nextState !== lastAnnouncedDetectionState) {
        const wasInitial = lastAnnouncedDetectionState === 'unknown';
        lastAnnouncedDetectionState = nextState;
        if (!wasInitial) {
          announceDetectionStatus(
            nextState === 'pass' ? 'Detection gate: ok' : 'Detection gate: weak',
          );
        }
      }
    }

    // Research aid: when both an envelope-derived peak and a raw best-range
    // exist, log a single debug line if they disagree by more than ~5 cm.
    if (UI_DEBUG && envelopePeak !== null && envelopePeak !== undefined
        && Number.isFinite(profile.bestRange) && profile.bestBin >= 0) {
      const delta = Math.abs(envelopePeak.range - profile.bestRange);
      if (delta > 0.05) {
        console.debug(`[ping:complete] envelope-vs-raw peak disagreement: env=${envelopePeak.range.toFixed(3)}m raw=${profile.bestRange.toFixed(3)}m delta=${delta.toFixed(3)}m`);
      }
    }

    if (UI_DEBUG) {
      let bMin = Infinity, bMax = -Infinity, bNonZero = 0;
      for (let i = 0; i < profile.bins.length; i++) {
        if (profile.bins[i] < bMin) bMin = profile.bins[i];
        if (profile.bins[i] > bMax) bMax = profile.bins[i];
        if (profile.bins[i] > 1e-15) bNonZero++;
      }
      console.debug(`[ping:complete] bins min=${bMin.toExponential(3)} max=${bMax.toExponential(3)} nonZero=${bNonZero}/${profile.bins.length}`);
    }

    const state = store.get();
    const isScanning = state.scanning;

    // During scanning, only update the heatmap progressively.
    // Skip profile/geometry/readout updates to prevent target instability —
    // those will be updated once at scan completion with the consensus result.
    if (!isScanning) {
      const lp = state.lastProfile;
      if (lp.corr && lp.corr.length > 0) {
        // Check if correlation has meaningful signal (not zeroed out)
        let corrHasSignal = false;
        for (let i = 0; i < lp.corr.length; i++) {
          if (Math.abs(lp.corr[i]) > 1e-12) { corrHasSignal = true; break; }
        }
        if (corrHasSignal) {
          if (UI_DEBUG) console.debug(`[ping:complete] drawing profile corr.length=${lp.corr.length} tau0=${lp.tau0} c=${lp.c}`);
          drawProfile(lp.corr, lp.tau0, lp.c, lp.minR, lp.maxR);
        } else {
          if (UI_DEBUG) console.debug('[ping:complete] no TX detected — showing placeholder');
          drawProfilePlaceholder();
        }
      } else {
        console.warn('[ping:complete] no corr in lastProfile');
        drawProfilePlaceholder();
      }
    }

    // Update heatmap row for current angle (works for both single Ping and Scan)
    const heatmap = state.heatmap;
    if (heatmap) {
      const rowIdx = heatmap.angles.indexOf(angleDeg);
      if (UI_DEBUG) console.debug(`[ping:complete] heatmap lookup: angleDeg=${angleDeg} rowIdx=${rowIdx} angles=${JSON.stringify(heatmap.angles)}`);
      if (rowIdx >= 0) {
        // Skip heatmap update if profile is empty (no valid detection)
        if (profile.bestBin >= 0 && profile.bestStrength > 1e-12) {
          updateHeatmapRow(heatmap, rowIdx, profile.bins, profile.bestBin, profile.bestStrength);
        } else {
          if (UI_DEBUG) console.debug(`[ping:complete] skipping heatmap update — no valid detection (bestBin=${profile.bestBin}, bestStrength=${profile.bestStrength.toExponential(3)})`);
        }
      } else {
        console.warn(`[ping:complete] angle ${angleDeg} not found in heatmap angles`);
      }
    } else {
      console.warn('[ping:complete] no heatmap in store');
    }

    drawHeatmap(state.config.minRange, state.config.maxRange);

    // During scanning, skip geometry/readout updates to avoid target jumping.
    // The scan-complete handler will do the final update.
    if (!isScanning) {
      drawGeometry(state.config.minRange, state.config.maxRange);
      updateBestReadout();
      updateDirectionReadout();

      if (profile.bestBin < 0 || profile.bestStrength <= 1e-12) {
        setStatusHint('No echo detected — raise <b>Output gain</b>, reduce noise, or aim at a hard, flat surface within 0.3–5 m.', 'warn');
      } else if (Number.isFinite(profile.bestRange)) {
        setStatusHint(`Echo detected at <b>${profile.bestRange.toFixed(2)} m</b>. Click <b>Scan</b> to map every direction.`, 'ok');
      }
    }

    // Ping stats
    const elapsed = (performance.now() - pingStartTime).toFixed(0);
    const peak = profile.bestStrength;
    const bins = profile.bins;
    if (bins && bins.length > 0) {
      const statsEl = el('pingStats');
      if (profile.bestBin < 0) {
        // No valid detection (muted speakers / noise only)
        if (statsEl) {
          statsEl.style.display = 'inline-block';
          statsEl.textContent = `Ping: ${elapsed}ms | no signal detected`;
        }
      } else {
        const sorted = Float32Array.from(bins).sort();
        const median = sorted[Math.floor(sorted.length / 2)];
        const snr = median > 1e-12 ? 10 * Math.log10(peak / median) : 0;
        if (statsEl) {
          statsEl.style.display = 'inline-block';
          statsEl.textContent = `Ping: ${elapsed}ms | peak: ${peak.toFixed(4)} | SNR: ${snr.toFixed(1)} dB`;
        }
      }
    }
  });

  // Peer network UI
  setupPeerUI();

  // Auto-connect from URL params (QR code pairing)
  const offerParam = getOfferFromUrl();
  if (offerParam) {
    clearSignalFromUrl();
    handleUrlOffer(offerParam);
  } else {
    const answerParam = getAnswerFromUrl();
    if (answerParam) {
      clearSignalFromUrl();
      handleUrlAnswer(answerParam);
    }
  }

  // Resize + orientation change
  window.addEventListener('resize', () => {
    if (applyRetinaCanvases()) redrawAllCanvases();
  });
  // Some mobile browsers fire orientationchange without a resize event
  window.addEventListener('orientationchange', () => {
    // Delay slightly to let the browser finish layout reflow
    setTimeout(() => {
      if (applyRetinaCanvases()) redrawAllCanvases();
    }, 150);
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') resumeIfSuspended().catch(() => {});
  });

  // iOS bfcache: page may be restored from cache with suspended AudioContext
  window.addEventListener('pageshow', (ev) => {
    if ((ev as PageTransitionEvent).persisted) resumeIfSuspended().catch(() => {});
  });

  // Initial render
  setStatus('idle');
  syncGeometryWizardControls();
  applyRetinaCanvases();
  drawProfilePlaceholder();

  const savedPreset = localStorage.getItem('echoscope:devicePreset');
  const deviceKey = (savedPreset && DEVICE_PRESETS[savedPreset]) ? savedPreset : detectDevice();
  applyDevicePreset(deviceKey, true);
  // Preset may update DOM/store; normalize back to single source of truth
  readConfigFromDOM();
  syncDOMFromConfig();
  syncModeUI();
  syncModeSegmented();
  log(`[init] device: ${deviceKey}${savedPreset === deviceKey ? ' (saved)' : ''}`);

  const defaultAngles = [-60, -50, -40, -30, -20, -10, 0, 10, 20, 30, 40, 50, 60];
  store.set('heatmap', createHeatmap(defaultAngles, DEFAULT_HEAT_BINS));

  const config = store.get().config;
  drawHeatmap(config.minRange, config.maxRange);
  drawGeometry(config.minRange, config.maxRange);
  drawSanityPlaceholder();
  drawSignalPreview();

  refreshDeviceInfo().catch(() => {});
  renderCalibInfo();

  // Show onboarding overlay for first-time visitors
  initOnboarding();
}
